import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { ActivityLogsDrawer } from "#src/batteries-included-components/Drawers/ActivityLogsDrawer";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useAllResourceQuery } from "#src/components/hooks/adapters/useActivityLogs";
import useLocalization from "#src/hooks/useLocalization";
import {
  getActionForDisplay,
  getListOfChangedAttributes,
} from "#src/routes/change-log/ActivityLogsPage.helpers";
import { getLookupLink } from "#src/utils/display";
import { useQuery } from "@tanstack/react-query";
import {
  ArrayDataDisplay,
  DataTable,
  DataTablePanel,
  HeaderType,
  Link,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import {
  ActivityAction,
  ActivityAdapter,
  ActivityResource,
  ActivityResourceType,
  ActivitySchema,
  ActivityType,
  Resources,
  SortDirection,
  UserType,
  UsersAdapter,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

export const ActivityLogsTablePanel = ({
  resourceId,
  resourceType,
  title,
  viewConfigStorageKey,
  tableStateStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey" | "tableStateStorageKey"> & {
  resourceId?: string;
  resourceType?: ActivityResourceType;
  title: string;
}) => {
  const { localize } = useLocalization();
  const [filters] =
    useFilters<Parameters<typeof ActivityAdapter.getList>[0]["filters"]>(
      viewConfigStorageKey
    );
  const [selectedLog, setSelectedLog] = useState<ActivityType | null>(null);
  const [tableState, setTableState] = useTableSortingAndPagination(
    {
      sortBy: "timestamp",
      sortDirection: SortDirection.DESCENDING,
      pageSize: 25,
    },
    filters,
    tableStateStorageKey
  );
  const activitiesQueryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    // @ts-expect-error TODO: Typing on filter
    filters: {
      ...filters,
      ...(filters?.resource_id || resourceId
        ? { resource_id: filters?.resource_id ?? resourceId }
        : {}),
    },
    // @ts-expect-error TODO: Typing on filter
    meta: { resourceType: filters?.resource_type ?? resourceType },
  } satisfies Parameters<typeof ActivityAdapter.getList>[0];
  const activitiesQuery = useQuery(
    [Resources.ACTIVITY, activitiesQueryPayload],
    // @ts-expect-error TODO: Typing on filter
    () => ActivityAdapter.getList(activitiesQueryPayload),
    {
      staleTime: 0,
    }
  );
  const activities = activitiesQuery.data?.data ?? [];

  const processActivities = useMemo(() => {
    const uniqueUserIds = new Set<string>();
    const uniqueResourceIds = new Map(
      Object.values(ActivityResource).map((resource) => [
        resource,
        new Set<string>(),
      ])
    );

    activities?.forEach((activity: ActivityType) => {
      uniqueUserIds.add(activity.author_id);
      switch (activity.resource_type) {
        case ActivityResource.FACILITY:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.FACILITY)
              ?.add(activity.resource_id);
          }
          break;
        case ActivityResource.FLOW:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.FLOW)
              ?.add(activity.resource_id);
          }
          break;
        case ActivityResource.ASSET_GROUP:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.ASSET_GROUP)
              ?.add(activity.resource_id);
          }
          break;
        case ActivityResource.EQUIPMENT:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.EQUIPMENT)
              ?.add(activity.resource_id);
          }
          break;
        case ActivityResource.DEVICE:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.DEVICE)
              ?.add(activity.resource_id);
          }
          break;
        case ActivityResource.USER_GROUP:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.USER_GROUP)
              ?.add(activity.resource_id);
          }
          break;
        case ActivityResource.ROLE:
          if (activity.resource_id) {
            uniqueResourceIds
              .get(ActivityResource.ROLE)
              ?.add(activity.resource_id);
          }
          break;
      }
    });

    const uniqueUserIdsArray = Array.from(uniqueUserIds);

    return {
      uniqueUserIdsArray,
      uniqueResourceIds,
    };
  }, [activities]);

  const { uniqueUserIdsArray, uniqueResourceIds } = processActivities;
  const { resourceIdsToResourceDetailsMap, isLoadingResources } =
    useAllResourceQuery({
      resourceIds: uniqueResourceIds,
    });

  const { data: usersQueryData, isLoading: isLoadingUsers } = useQuery({
    queryKey: [Resources.USER, { filters: { id: uniqueUserIdsArray } }],
    queryFn: () =>
      UsersAdapter.getList({ filters: { id: uniqueUserIdsArray } }),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(uniqueUserIdsArray.length),
  });
  const users = usersQueryData?.data ?? [];
  const authorIdsToUserDetailsMap = useMemo(() => {
    return Object.fromEntries(users.map((user) => [user.id, user])) as Record<
      string,
      UserType
    >;
  }, [users]);

  const activityModelKeys = ActivitySchema.keyof().Enum;
  const isLoading =
    activitiesQuery.isLoading ||
    (Boolean(uniqueUserIdsArray.length) && isLoadingUsers) ||
    isLoadingResources;
  const headers: Array<HeaderType<ActivityType>> = [
    {
      label: "Type",
      key: activityModelKeys.action,
      renderComponent: ({ item }) => {
        if (!item) {
          return null;
        }

        const transformedAction = toTitleCaseString(
          getActionForDisplay(item) ?? "",
          {
            intent: "ui",
          }
        );
        const transformedAssetType = toTitleCaseString(
          localize(item?.resource_type),
          {
            intent: "ui",
          }
        );
        const actionLabel = `${transformedAssetType} ${transformedAction}`;

        return (
          <Link
            label={actionLabel}
            onClick={() => setSelectedLog(item)}
            isLoading={isLoading}
          />
        );
      },
    },
    {
      label: "Updated Attributes",
      key: "updated_attributes",
      renderComponent: ({ item }) => (
        <ArrayDataDisplay value={getListOfChangedAttributes(item) ?? []} />
      ),
    },
    {
      label: "Resource ID",
      key: activityModelKeys.resource_id,
    },
    {
      label: "Resource Name",
      key: "name",
      renderComponent: ({ item }) => {
        const isResourceDeleted = item.action === ActivityAction.DELETE;
        const isResourceCreated = item.action === ActivityAction.CREATE;
        const resourceName = isResourceDeleted
          ? (item?.before as { name: string })?.name
          : isResourceCreated
            ? (item?.after as { name: string })?.name
            : resourceIdsToResourceDetailsMap[item.resource_id]?.name;
        return resourceName
          ? getLookupLink(item.resource_id, resourceName, item.resource_type)
          : "-";
      },
    },
    {
      label: "Resource Type",
      key: activityModelKeys.resource_type,
      renderComponent: ({ item }) =>
        item
          ? toTitleCaseString(localize(item?.resource_type), { intent: "ui" })
          : null,
    },
    {
      label: "By User",
      key: activityModelKeys.author_id,
      renderComponent: ({ item }) => {
        const id = item.author_id;
        const name = authorIdsToUserDetailsMap[id]?.name ?? "-";
        return id ? (
          <UserDataDisplay
            id={id}
            name={name}
            isLoading={isLoading}
          />
        ) : (
          "-"
        );
      },
    },
    {
      label: "Logged At",
      key: activityModelKeys.timestamp,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item[activityModelKeys.timestamp]}
          withTime
        />
      ),
    },
  ];

  return (
    <>
      <DataTablePanel
        storageKey={viewConfigStorageKey}
        dataTableProps={{
          headers,
          items: activitiesQuery.data?.data ?? [],
          getItemId: (item) => item.id,
          isLoading,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: activitiesQuery.data?.total_entries,
            pageSizeText: "logs per page",
          },
          onSortChange: setTableState,
          onPaginationChange: setTableState,
        }}
        panelProps={{
          title,
        }}
      />
      <ActivityLogsDrawer
        logDetails={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </>
  );
};
